/**
 * Un icono por linea de producto, para el menu del catalogo (rediseño del
 * 14-09-2026, a pedido del cliente y tomando el menu de imperial.cl como
 * referencia).
 *
 * Se eligen por el `slug` de la linea y no por su id: el slug es lo que se lee
 * y lo que cualquiera reconoce al agregar una linea nueva. Una linea sin icono
 * asignado recibe la caja generica, nunca un hueco, porque el catalogo de
 * Random (`RND-01`) puede traer lineas que este archivo todavia no conoce.
 *
 * Van en un archivo aparte de `Icono.tsx` para no engordar el conjunto de
 * iconos de la interfaz con 37 dibujos que solo se ven en un lugar. Mismas
 * reglas: SVG inline, `currentColor`, decorativos (`aria-hidden`): el nombre
 * lo pone siempre el texto de al lado.
 */
import type { ReactElement } from 'react';

const TRAZOS: Record<string, ReactElement> = {
  'accesorios-para-ciclistas': (
    <>
      <circle cx="6" cy="16" r="3.5" />
      <circle cx="18" cy="16" r="3.5" />
      <path d="M6 16l4-8h5l3 8M10 8h4l-2 8" />
    </>
  ),
  'apriete-y-sujecion-de-la-madera': (
    <>
      <path d="M5 4h9v3H9v8h5v3H5z" />
      <path d="M14 8.5h4M14 13.5h4M18 5v13" />
    </>
  ),
  'articulos-de-carpinteria': (
    <>
      <path d="M3.5 7.5l4-4 6 6-4 4z" />
      <path d="M11.5 11.5l8.5 8.5" />
    </>
  ),
  'articulos-de-ebanisteria': (
    <>
      <path d="M3 15h18l-2 4H5z" />
      <path d="M8 15V9h5v6M10.5 9V5.5" />
    </>
  ),
  'articulos-de-ferreteria': (
    <path d="M14.5 4.5a4 4 0 0 0-4.8 5.2L3.5 16v3.5H7l6.3-6.2a4 4 0 0 0 5.2-4.8l-2.6 2.6-2.4-.7-.7-2.4z" />
  ),
  bisagras: (
    <>
      <rect x="3.5" y="5" width="7" height="14" rx="1" />
      <rect x="13.5" y="5" width="7" height="14" rx="1" />
      <path d="M12 5v14M7 9.5h.01M7 14.5h.01M17 9.5h.01M17 14.5h.01" />
    </>
  ),
  cerraduras: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3M12 14.5v2.5" />
    </>
  ),
  'cierres-de-joyeria-y-cofre': (
    <>
      <path d="M3.5 10h17v9.5h-17z" />
      <path d="M3.5 10l2-4.5h13l2 4.5M10 10v3h4v-3" />
    </>
  ),
  'cintas-y-adhesivos-aplicados': (
    <>
      <circle cx="11" cy="11" r="7.5" />
      <circle cx="11" cy="11" r="2.5" />
      <path d="M17.5 14.5l3.5 1.5v4h-9" />
    </>
  ),
  'componentes-puertas-y-ventanas': (
    <>
      <rect x="5" y="3.5" width="14" height="17" rx="1" />
      <path d="M5 20.5h14M15.5 12h.01" />
    </>
  ),
  'correderas-y-cajones': (
    <>
      <rect x="3.5" y="5" width="17" height="6" rx="1" />
      <rect x="3.5" y="13" width="17" height="6" rx="1" />
      <path d="M10 8h4M10 16h4" />
    </>
  ),
  'el-mundo-de-la-tapiceria': (
    <>
      <path d="M5 11V8a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v3" />
      <path d="M3.5 13a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v5h-17z" />
      <path d="M6 18v2M18 18v2" />
    </>
  ),
  'film-para-termolaminado': (
    <>
      <rect x="4" y="4" width="16" height="16" rx="1.5" />
      <path d="M4 9c2.5-1.5 5.5-1.5 8 0s5.5 1.5 8 0M4 15c2.5-1.5 5.5-1.5 8 0s5.5 1.5 8 0" />
    </>
  ),
  'fresas-e-instrumentos-de-corte': (
    <>
      <path d="M12 3v6" />
      <path d="M8.5 9h7l1.5 5-5 6.5L7 14z" />
      <path d="M12 9v11.5" />
    </>
  ),
  'herrajes-decorativos': (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v4M12 16.5v4M3.5 12h4M16.5 12h4M6 6l2.8 2.8M15.2 15.2L18 18M18 6l-2.8 2.8M8.8 15.2L6 18" />
    </>
  ),
  'herrajes-para-exhibidores': (
    <>
      <path d="M4 5h16M12 5v4" />
      <path d="M8 9h8l-1.5 7h-5z" />
      <path d="M12 16v4" />
    </>
  ),
  'herrajes-para-la-construccion': (
    <>
      <path d="M5 4v15h15" />
      <path d="M5 19l15-15" />
      <path d="M8.5 8h.01M8.5 15.5h.01M16 15.5h.01" />
    </>
  ),
  'herrajes-para-vidrios': (
    <>
      <rect x="4" y="4" width="16" height="16" rx="1" />
      <path d="M7 15l6-8M11 17l3-4" />
      <path d="M2.5 12h3M18.5 12h3" />
    </>
  ),
  'herramientas-para-el-jardin': (
    <>
      <path d="M12 21V11" />
      <path d="M12 11c-5 0-7.5-3-8-8 5 .5 8 3 8 8z" />
      <path d="M12 14c0-4 2.5-6.5 7-7-.5 4.5-3 7-7 7z" />
    </>
  ),
  'maquinas-chansen-warrior': (
    <>
      <path d="M3 13h18v3H3z" />
      <path d="M5 16v4M19 16v4" />
      <circle cx="12" cy="9" r="4.5" />
      <path d="M12 4.5v9" />
    </>
  ),
  'maquinas-para-uso-industrial': (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v2.4M12 18.6V21M3 12h2.4M18.6 12H21M5.6 5.6l1.7 1.7M16.7 16.7l1.7 1.7M18.4 5.6l-1.7 1.7M7.3 16.7l-1.7 1.7" />
      <circle cx="12" cy="12" r="6.6" />
    </>
  ),
  'grifos-lavabos-y-fregaderos': (
    <>
      <path d="M12 3.5v5M8.5 5h7" />
      <path d="M12 8.5a4 4 0 0 1 4 4v1.5" />
      <path d="M4 15h16v1.5a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z" />
    </>
  ),
  'pegamento-masilla-y-aislantes': (
    <>
      <path d="M10 3.5h4v3h-4z" />
      <path d="M9 6.5h6l1.5 4v9.5h-9V10.5z" />
      <path d="M12 13v4" />
    </>
  ),
  'pisos-guardapolvos-cubrejuntas': (
    <>
      <path d="M3.5 17.5h17" />
      <path d="M3.5 14.5h17v3h-17z" />
      <path d="M6 14.5V8.5h4v6M10 14.5V5.5h4v9M14 14.5V11h4v3.5" />
    </>
  ),
  'productos-acabado-y-abrasivos': (
    <>
      <path d="M4 15.5h16v4H4z" />
      <path d="M7 15.5V9h10v6.5" />
      <path d="M12 3.5v2M8.5 5l1 1.7M15.5 5l-1 1.7" />
    </>
  ),
  'proteccion-de-la-madera': (
    <>
      <path d="M5 8.5h14v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z" />
      <path d="M8 8.5V6a4 4 0 0 1 8 0v2.5" />
      <path d="M5 13c2.3 1.2 4.7 1.2 7 0s4.7-1.2 7 0" />
    </>
  ),
  'quincalleria-especial': (
    <>
      <circle cx="8" cy="9" r="4.5" />
      <path d="M11.2 12.2L20 21M16 17l2.5-2.5M13.5 14.5l2.5-2.5" />
    </>
  ),
  ruedas: (
    <>
      <path d="M8 3.5h8M12 3.5v4" />
      <path d="M8.5 7.5h7v4h-7z" />
      <circle cx="12" cy="16" r="4.5" />
      <circle cx="12" cy="16" r="1.2" />
    </>
  ),
  'seguridad-proteccion-personal': (
    <>
      <path d="M4 15a8 8 0 0 1 16 0" />
      <path d="M3 15h18v2.5H3z" />
      <path d="M12 7v-3" />
    </>
  ),
  'sistemas-de-apertura': (
    <>
      <rect x="3.5" y="11" width="17" height="9" rx="1" />
      <path d="M12 8V3M8.5 6.5L12 3l3.5 3.5" />
    </>
  ),
  'soluciones-de-armario-y-closet': (
    <>
      <path d="M12 4a2 2 0 1 1 2 2c-1 0-2 .8-2 2v1" />
      <path d="M12 9l-8.5 6.5a1 1 0 0 0 .6 1.8h15.8a1 1 0 0 0 .6-1.8z" />
    </>
  ),
  'soluciones-de-iluminacion': (
    <>
      <path d="M9 18h6M10 21h4" />
      <path d="M8 14a6 6 0 1 1 8 0c-.9.8-1.5 1.8-1.5 3h-5c0-1.2-.6-2.2-1.5-3z" />
    </>
  ),
  'soluciones-para-cuartos-y-bano': (
    <>
      <path d="M4 12h16v3a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5z" />
      <path d="M6 12V6.5a2.5 2.5 0 0 1 5 0" />
      <path d="M7 20v1.5M17 20v1.5" />
    </>
  ),
  'soluciones-para-muebles': (
    <>
      <rect x="4" y="3.5" width="16" height="17" rx="1" />
      <path d="M12 3.5v17M4 12h16M9.5 8v1.5M14.5 8v1.5M9.5 15v1.5M14.5 15v1.5" />
    </>
  ),
  'soluciones-para-oficina': (
    <>
      <rect x="3.5" y="4" width="17" height="11" rx="1.5" />
      <path d="M12 15v4M8 19h8" />
    </>
  ),
  'soluciones-para-tu-cocina': (
    <>
      <path d="M5 10h14v6a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3z" />
      <path d="M2.5 12h2.5M19 12h2.5" />
      <path d="M9 7c0-1.5 1-2 1-3.5M13 7c0-1.5 1-2 1-3.5" />
    </>
  ),
  'suministros-para-el-taller': (
    <>
      <rect x="3.5" y="8" width="17" height="12" rx="1.5" />
      <path d="M9 8V5.5h6V8M3.5 13h17M12 11.5v3" />
    </>
  ),
  'tableros-paneles-y-tapacantos': (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="1" />
      <path d="M6.5 9c3-1 5-1 8 0s5 1 3 0M6.5 15c3-1 5-1 8 0s5 1 3 0" />
    </>
  ),
  'tornillos-y-fijaciones': (
    <>
      <path d="M8.5 4h7v3h-7z" />
      <path d="M12 4v-1M10 7v13l2 1 2-1V7" />
      <path d="M10 10l4 1.5M10 13.5l4 1.5M10 17l4 1.5" />
    </>
  ),
};

/**
 * Slugs que Random escribe distinto de como los tenia el sitio y nombran la
 * misma linea. La sincronizacion (RND-01) crea la linea con el slug del nombre
 * de Random —"Apriete, Sujecion" en vez de "Apriete y Sujecion", "Chansen y
 * Warrior" en vez de "Chansen & Warrior"—, y sin esto perdian su icono.
 */
const ALIAS: Record<string, string> = {
  'apriete-sujecion-de-la-madera': 'apriete-y-sujecion-de-la-madera',
  'maquinas-chansen-y-warrior': 'maquinas-chansen-warrior',
};

const GENERICO = (
  <>
    <path d="M3 7.5l9-4.5 9 4.5v9L12 21l-9-4.5z" />
    <path d="M3 7.5l9 4.5 9-4.5M12 12v9" />
  </>
);

export interface IconoLineaProps {
  slug: string;
  className?: string;
}

export function IconoLinea({ slug, className }: IconoLineaProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...(className !== undefined ? { className } : {})}
    >
      {TRAZOS[ALIAS[slug] ?? slug] ?? GENERICO}
    </svg>
  );
}
